import { supabase } from '../lib/clients/supabase';

/**
 * Fetches all bookings with related data for admin view
 * @param {Object} options - Query options for pagination, filtering, etc.
 * @returns {Promise<{data: Array, error: Object}>}
 */
export const fetchAllBookings = async (options = {}) => {
  try {
    const { page = 1, limit = 10, status, startDate, endDate } = options;
    const start = (page - 1) * limit;

    // First, get the total count with the same filters
    let countQuery = supabase
      .from('bookings')
      .select('id', { count: 'exact', head: true });

    // Apply filters to count query
    if (status) {
      countQuery = countQuery.eq('status', status);
    }
    if (startDate) {
      countQuery = countQuery.gte('booking_date', startDate);
    }
    if (endDate) {
      countQuery = countQuery.lte('booking_date', endDate);
    }

    const { count, error: countError } = await countQuery;

    if (countError) throw countError;

    // Then fetch the actual data
    let query = supabase
      .from('bookings')
      .select(`
        *,
        profiles:user_id (id, first_name, last_name, email, phone_number),
        guest_profiles:guest_profile_id (id, first_name, last_name, email, phone_number),
        vehicle_types:vehicle_type_id (id, title, image_url),
        services:service_id (
          id, 
          name, 
          note,
          services_base_price (
            price,
            vehicle_types_id
          )
        ),
        booking_addons (
          service_addons (id, name, price)
        )
      `)
      .order('booking_date', { ascending: true })
      .range(start, start + limit - 1);

    // Apply filters if provided
    if (status) {
      query = query.eq('status', status);
    }
    if (startDate) {
      query = query.gte('booking_date', startDate);
    }
    if (endDate) {
      query = query.lte('booking_date', endDate);
    }

    const { data, error } = await query;

    if (error) throw error;

    return {
      data,
      totalCount: count || 0,
      currentPage: page,
      error: null
    };
  } catch (error) {
    console.error('Error fetching all bookings:', error);
    return {
      data: null,
      totalCount: 0,
      currentPage: 1,
      error: {
        message: 'Failed to fetch bookings',
        details: error.message
      }
    };
  }
};

/**
 * Fetches bookings for a specific user
 * @param {string} userId - The user's ID
 * @param {Object} options - Query options for pagination, filtering, etc.
 * @returns {Promise<{data: Array, error: Object}>}
 */
export const fetchUserBookings = async (userId, options = {}) => {
  try {
    const { page = 1, limit = 10, status } = options;
    const start = (page - 1) * limit;

    // First, get the total count with the same filters
    let countQuery = supabase
      .from('bookings')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (status) {
      countQuery = countQuery.eq('status', status);
    }

    const { count, error: countError } = await countQuery;

    if (countError) throw countError;

    // Then fetch the actual data
    let query = supabase
      .from('bookings')
      .select(`
        *,
        profiles:user_id (id, first_name, last_name, email, phone_number),
        guest_profiles:guest_profile_id (id, first_name, last_name, email, phone_number),
        vehicle_types:vehicle_type_id (id, title, image_url),
        services:service_id (
          id, 
          name, 
          note,
          services_base_price (
            price,
            vehicle_types_id
          )
        ),
        booking_addons (
          service_addons (id, name, price)
        )
      `)
      .eq('user_id', userId)
      .order('booking_date', { ascending: false })
      .range(start, start + limit - 1);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;

    if (error) throw error;

    return {
      data,
      totalCount: count || 0,
      currentPage: page,
      error: null
    };
  } catch (error) {
    console.error('Error fetching user bookings:', error);
    return {
      data: null,
      totalCount: 0,
      currentPage: 1,
      error: {
        message: 'Failed to fetch user bookings',
        details: error.message
      }
    };
  }
};

/**
 * Creates a new booking
 * @param {Object} bookingData - The booking data
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const createBooking = async (bookingData) => {
  try {
    // First create the booking
    const { data: booking, error: bookingError } = await supabase
      .from('bookings')
      .insert([{
        user_id: bookingData.userId,
        vehicle_type_id: bookingData.vehicleTypeId,
        service_id: bookingData.serviceId,
        booking_date: bookingData.date,
        booking_time: bookingData.time,
        status: bookingData.status || 'pending',
        location_service: bookingData.location_service,
        address: bookingData.address,
        total_price: bookingData.totalPrice
      }])
      .select()
      .single();

    if (bookingError) throw bookingError;

    // If there are add-ons, create the booking_addons entries
    if (bookingData.addons && bookingData.addons.length > 0) {
      const addonEntries = bookingData.addons.map(addonId => ({
        booking_id: booking.id,
        addon_id: addonId
      }));

      const { error: addonsError } = await supabase
        .from('booking_addons')
        .insert(addonEntries);

      if (addonsError) throw addonsError;
    }

    return { data: booking, error: null };
  } catch (error) {
    console.error('Error creating booking:', error);
    return {
      data: null,
      error: {
        message: 'Failed to create booking',
        details: error.message
      }
    };
  }
};

/**
 * Updates a booking's status
 * @param {string} bookingId - The booking ID
 * @param {string} status - The new status
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const updateBookingStatus = async (bookingId, status) => {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .update({ status, updated_at: new Date() })
      .eq('id', bookingId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error updating booking status:', error);
    return {
      data: null,
      error: {
        message: 'Failed to update booking status',
        details: error.message
      }
    };
  }
};

/**
 * Deletes a booking
 * @param {string} bookingId - The booking ID
 * @returns {Promise<{success: boolean, error: Object}>}
 */
export const deleteBooking = async (bookingId) => {
  try {
    // First delete related add-ons
    const { error: addonsError } = await supabase
      .from('booking_addons')
      .delete()
      .eq('booking_id', bookingId);

    if (addonsError) throw addonsError;

    // Then delete the booking
    const { error: bookingError } = await supabase
      .from('bookings')
      .delete()
      .eq('id', bookingId);

    if (bookingError) throw bookingError;

    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting booking:', error);
    return {
      success: false,
      error: {
        message: 'Failed to delete booking',
        details: error.message
      }
    };
  }
};

/**
 * Checks if a user exists by email
 * @param {string} email - The email to check
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export const findUserByEmail = async (email) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email')
      .eq('email', email)
      .maybeSingle();
    
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error('Error finding user by email:', error);
    return {
      data: null,
      error: {
        message: 'Failed to find user',
        details: error.message
      }
    };
  }
};

/**
 * Finds a guest profile by email
 * @param {string} email - The email to check
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export const findGuestProfileByEmail = async (email) => {
  try {
    const { data, error } = await supabase
      .from('guest_profiles')
      .select('id, email')
      .eq('email', email)
      .maybeSingle();
    
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error('Error finding guest profile by email:', error);
    return {
      data: null,
      error: {
        message: 'Failed to find guest profile',
        details: error.message
      }
    };
  }
};

/**
 * Creates a new guest profile
 * @param {Object} profileData - The guest profile data
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export const createGuestProfile = async (profileData) => {
  try {
    const { data, error } = await supabase
      .from('guest_profiles')
      .insert([{
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        email: profileData.email,
        phone_number: profileData.phone
      }])
      .select()
      .single();
    
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error('Error creating guest profile:', error);
    return {
      data: null,
      error: {
        message: 'Failed to create guest profile',
        details: error.message
      }
    };
  }
};

/**
 * Creates booking addons using RPC for admin
 * @param {Array} addonRecords - Array of addon records
 * @returns {Promise<{success: boolean, error: Object|null}>}
 */
export const createBookingAddonsAsAdmin = async (addonRecords) => {
  try {
    const { error } = await supabase.rpc('admin_insert_booking_addons', {
      addon_records: addonRecords
    });
    
    if (error) throw error;
    
    return { success: true, error: null };
  } catch (error) {
    console.error('Error creating booking addons as admin:', error);
    return {
      success: false,
      error: {
        message: 'Failed to create booking addons',
        details: error.message
      }
    };
  }
};

/**
 * Creates booking addons directly
 * @param {Array} addonRecords - Array of addon records
 * @returns {Promise<{success: boolean, error: Object|null}>}
 */
export const createBookingAddons = async (addonRecords) => {
  try {
    const { error } = await supabase
      .from('booking_addons')
      .insert(addonRecords);
    
    if (error) throw error;
    
    return { success: true, error: null };
  } catch (error) {
    console.error('Error creating booking addons:', error);
    return {
      success: false,
      error: {
        message: 'Failed to create booking addons',
        details: error.message
      }
    };
  }
};

/**
 * Creates a complete booking with user/guest profile and addons
 * @param {Object} bookingDetails - Complete booking details
 * @param {string} userRole - The role of the user creating the booking
 * @param {string|null} currentUserId - The ID of the current user
 * @returns {Promise<{success: boolean, data: Object|null, error: Object|null}>}
 */
export const createCompleteBooking = async (bookingDetails, userRole, currentUserId) => {
  try {
    let userId = null;
    let guestProfileId = null;

    // If user is logged in, use their ID
    if (currentUserId) {
      userId = currentUserId;
    } else {
      // For guest users, create a guest profile
      const { data: newGuest, error: newGuestError } = await supabase
        .from('guest_profiles')
        .insert([{
          first_name: bookingDetails.firstName,
          last_name: bookingDetails.lastName,
          email: bookingDetails.email,
          phone_number: bookingDetails.phone,
          created_at: new Date().toISOString()
        }])
        .select('id')
        .single();
      
      if (newGuestError) {
        console.error('Error creating guest profile:', newGuestError);
        throw newGuestError;
      }
      guestProfileId = newGuest.id;
    }

    // Create the booking
    const bookingData = {
      vehicle_type_id: bookingDetails.vehicleTypeId,
      service_id: bookingDetails.serviceId,
      booking_date: bookingDetails.date,
      booking_time: bookingDetails.time,
      status: 'Pending',
      total_price: bookingDetails.totalPrice,
      created_at: new Date().toISOString(),
      location_service: bookingDetails.locationService,
      address: bookingDetails.address
    };

    // Set either user_id or guest_profile_id
    if (userId) {
      bookingData.user_id = userId;
      bookingData.guest_profile_id = null;
    } else {
      bookingData.guest_profile_id = guestProfileId;
      bookingData.user_id = null;
    }

    // Create the booking with minimal select to avoid permission issues
    const { data: booking, error: bookingError } = await supabase
      .from('bookings')
      .insert([bookingData])
      .select('id, booking_date, booking_time, status, total_price, location_service, address')
      .single();

    if (bookingError) {
      console.error('Error creating booking:', bookingError);
      // If booking fails and we created a new guest profile, delete it
      if (guestProfileId) {
        await supabase
          .from('guest_profiles')
          .delete()
          .eq('id', guestProfileId);
      }
      throw bookingError;
    }

    // Add addons if any
    let addonsSuccess = true;
    if (bookingDetails.addons && bookingDetails.addons.length > 0) {
      try {
        // Create each addon separately to avoid batch failures
        for (const addonId of bookingDetails.addons) {
          const { error } = await supabase
            .from('booking_addons')
            .insert({
              booking_id: booking.id,
              addon_id: addonId
            });
          
          if (error) {
            console.error(`Error adding addon ${addonId}:`, error);
            addonsSuccess = false;
          }
        }
      } catch (addonError) {
        console.error('Error adding addons:', addonError);
        addonsSuccess = false;
      }
    }

    // Update availability table
    try {
      const { data: existingAvailability } = await supabase
        .from('availability')
        .select('time_slots')
        .eq('date', bookingDetails.date)
        .maybeSingle();

      const timeSlot = {
        available: false,
        booking_id: booking.id
      };

      if (existingAvailability) {
        const updatedTimeSlots = {
          ...existingAvailability.time_slots,
          [bookingDetails.time]: timeSlot
        };

        await supabase
          .from('availability')
          .update({ 
            time_slots: updatedTimeSlots,
            updated_at: new Date().toISOString()
          })
          .eq('date', bookingDetails.date);
      } else {
        await supabase
          .from('availability')
          .insert([{
            date: bookingDetails.date,
            time_slots: {
              [bookingDetails.time]: timeSlot
            }
          }]);
      }
    } catch (availabilityError) {
      console.error('Failed to update availability:', availabilityError);
      // Don't throw since booking was successful
    }

    return { 
      success: true, 
      data: booking,
      error: null,
      warnings: !addonsSuccess ? ["Some add-ons could not be added to the booking"] : []
    };
    
  } catch (error) {
    console.error('Error creating complete booking:', error);
    return {
      success: false,
      data: null,
      error: {
        message: 'Failed to create booking',
        details: error.message
      }
    };
  }
};

/**
 * Updates a booking with new data
 * @param {string} bookingId - The booking ID
 * @param {Object} updatedData - The updated booking data
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const updateBooking = async (bookingId, updatedData) => {
  try {
    // Extracting only the fields that exist in the bookings table
    const {
      booking_date,
      booking_time,
      service_id,
      vehicle_type_id,
      total_price,
      status,
      notes,
      location_service,
      address,
      booking_addons, // This will be handled separately
      ...rest
    } = updatedData;

    // Prepare the booking update data
    const bookingUpdateData = {
      booking_date,
      booking_time,
      service_id,
      vehicle_type_id,
      total_price,
      status,
      notes,
      location_service,
      address,
      updated_at: new Date()
    };

    // First, update the booking details
    const { data: updatedBooking, error: bookingError } = await supabase
      .from('bookings')
      .update(bookingUpdateData)
      .eq('id', bookingId)
      .select()
      .single();

    if (bookingError) {
      console.error('Error updating booking:', bookingError);
      throw bookingError;
    }

    // Handle add-ons if they exist in the update
    if (booking_addons) {
      // First, get existing add-ons
      const { data: existingAddons, error: fetchError } = await supabase
        .from('booking_addons')
        .select('addon_id')
        .eq('booking_id', bookingId);

      if (fetchError) {
        console.error('Error fetching existing add-ons:', fetchError);
        throw fetchError;
      }

      const existingAddonIds = existingAddons?.map(addon => addon.addon_id) || [];
      const newAddonIds = booking_addons.map(addon => addon.addon_id);

      // Find add-ons to delete (existing but not in new list)
      const addonsToDelete = existingAddonIds.filter(id => !newAddonIds.includes(id));
      
      // Find add-ons to add (new but not in existing list)
      const addonsToAdd = newAddonIds.filter(id => !existingAddonIds.includes(id));

      // Delete removed add-ons
      if (addonsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('booking_addons')
          .delete()
          .eq('booking_id', bookingId)
          .in('addon_id', addonsToDelete);

        if (deleteError) {
          console.error('Error deleting removed add-ons:', deleteError);
          throw deleteError;
        }
      }

      // Add new add-ons
      if (addonsToAdd.length > 0) {
        const newAddons = addonsToAdd.map(addonId => ({
          booking_id: bookingId,
          addon_id: addonId
        }));

        const { error: insertError } = await supabase
          .from('booking_addons')
          .insert(newAddons);

        if (insertError) {
          console.error('Error inserting new add-ons:', insertError);
          throw insertError;
        }
      }
    }

    // Fetch the complete updated booking with all related data
    const { data: finalBooking, error: fetchError } = await supabase
      .from('bookings')
      .select(`
        *,
        profiles:user_id (id, first_name, last_name, email, phone_number),
        guest_profiles:guest_profile_id (id, first_name, last_name, email, phone_number),
        vehicle_types:vehicle_type_id (id, title, image_url),
        services:service_id (
          id, 
          name, 
          note,
          services_base_price (
            price,
            vehicle_types_id
          )
        ),
        booking_addons (
          addon_id,
          service_addons (
            id,
            name,
            price
          )
        )
      `)
      .eq('id', bookingId)
      .single();

    if (fetchError) {
      console.error('Error fetching updated booking:', fetchError);
      throw fetchError;
    }

    return { data: finalBooking, error: null };
  } catch (error) {
    console.error('Error in updateBooking:', error);
    return {
      data: null,
      error: {
        message: 'Failed to update booking',
        details: error.message
      }
    };
  }
};

/**
 * Fetches a booking with all related data including add-ons
 * @param {string} bookingId - The booking ID
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const fetchBookingWithDetails = async (bookingId) => {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        profiles:user_id (id, first_name, last_name, email, phone_number),
        guest_profiles:guest_profile_id (id, first_name, last_name, email, phone_number),
        vehicle_types:vehicle_type_id (id, title, image_url),
        services:service_id (
          id, 
          name, 
          note,
          services_base_price (
            price,
            vehicle_types_id
          )
        ),
        booking_addons (
          addon_id,
          service_addons (
            id,
            name,
            price
          )
        )
      `)
      .eq('id', bookingId)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching booking details:', error);
    return {
      data: null,
      error: {
        message: 'Failed to fetch booking details',
        details: error.message
      }
    };
  }
}; 